import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Zap,
  Award,
  UserCheck,
  Shield,
  Headphones,
  Trophy,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Advertising claims. The FTC requires a reasonable basis for objective
// performance claims before they run, and outcome claims about a product that
// affects someone's livelihood draw more scrutiny, not less. Anything with a
// number, a "prevents", or a count of successful users needs evidence on file
// that would survive being asked for. These were previously "95% Time
// Reduction", "We prevent auto-rejection", "Trained on successful applications
// that got offers", and "Join the hundreds of successful candidates who have
// found their dream jobs" — the last of which also contradicted Section 3 of
// our own Terms, which disclaims any guarantee of results. Keep claims here
// about what the software does, which is verifiable, rather than what it gets
// the user, which is not.
const reasons = [
  {
    icon: Zap,
    title: "Apply in One Swipe",
    desc: "Skip the forms. Swipe right and we complete and submit the employer's application for you.",
  },
  {
    icon: Award,
    title: "Written for Each Role",
    desc: "Cover letters and free-text answers are generated per application from your profile and that job's posting.",
  },
  {
    icon: UserCheck,
    title: "Filled From Your Profile",
    desc: "Your resume, work history, and preferences populate every field, so you enter them once instead of on every site.",
  },
  {
    icon: Shield,
    title: "Matched to the Job Description",
    desc: "We read each posting and reflect the skills and terms it actually asks for, instead of sending the same application everywhere.",
  },
  {
    icon: Headphones,
    title: "Real Support",
    desc: "Email us at contact@jobharvest.com and a person will get back to you.",
  },
  {
    icon: Trophy,
    title: "Everything in One Place",
    desc: "Track every application through screen, interview, and offer, with employer replies collected in your inbox.",
  },
];

const CinematicProtocol = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".cinematic-reasons__header",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cinematic-reasons__header",
            start: "top 80%",
          },
        }
      );

      gsap.fromTo(
        ".cinematic-reasons__item",
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cinematic-reasons__list",
            start: "top 80%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="cinematic-reasons" id="whyus" ref={sectionRef}>
      <div className="cinematic-reasons__inner">
        <div className="cinematic-reasons__header">
          <p className="cinematic-reasons__label">Why Choose JobHarvest</p>
          <h2 className="cinematic-reasons__title">
            Our focus on the applicant,{" "}
            <span className="cinematic-reasons__title-drama">
              not the employer.
            </span>
          </h2>
          <p className="cinematic-reasons__subtitle">
            We built every feature to maximize your chances of landing
            interviews.
          </p>
        </div>

        <div className="cinematic-reasons__list">
          {reasons.map((reason, i) => (
            <div key={reason.title} className="cinematic-reasons__item">
              <div className="cinematic-reasons__item-number">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="cinematic-reasons__item-icon">
                <reason.icon size={22} strokeWidth={1.8} />
              </div>
              <div className="cinematic-reasons__item-content">
                <h3 className="cinematic-reasons__item-title">
                  {reason.title}
                </h3>
                <p className="cinematic-reasons__item-desc">{reason.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CinematicProtocol;
